import axios, { AxiosInstance, AxiosResponse } from 'axios';

// API Configuration
export const API_CONFIG = {
  BASE_URL: 'https://server.bitsindia.in/api',
  TIMEOUT: 120000,
  HEADERS: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer demo-token',
  },
};

// Create axios instance with default configuration
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add any request modifications here (e.g., auth tokens)
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    
    // Handle common error scenarios
    if (error.response) {
      // Server responded with error status
      switch (error.response.status) {
        case 401:
          break;
        case 403:
          break;
        case 404:
          break;
        case 500:
          break;
        default:
      }
    } else if (error.request) {
      // Network error
    }
    
    return Promise.reject(error);
  }
);

// Generic API response interface
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
}

// API Error interface
export interface ApiError {
  message: string;
  status?: number;
  details?: any;
}

// Bulk Analysis Interfaces
export interface BulkAnalysisConfig {
  enableLLMAnalysis?: boolean;
  enableImageAnalysis?: boolean;
  enableTextAnalysis?: boolean;
  confidenceThreshold?: number;
  autoMatchProducts?: boolean;
  priority?: 'low' | 'medium' | 'high';
  batchSize?: number;
}

export interface BulkAnalysisQuery {
  query_type: 'url' | 'csv';
  query_content?: string;
  urls?: string[];
  csv_file?: File;
  llm_provider?: 'openai' | 'groq';
  max_images_per_product?: number;
  priority?: 'low' | 'medium' | 'high';
  enableLLMAnalysis?: boolean;
  enableImageAnalysis?: boolean;
  enableTextAnalysis?: boolean;
  confidenceThreshold?: number;
  autoMatchProducts?: boolean;
  batchSize?: number;
}

export interface BulkAnalysisStatus {
  id: string;
  type: 'url' | 'csv';
  content: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  progress_percentage: number;
  current_step: string;
  total_products: number;
  products_processed: number;
  successful_analyses: number;
  failed_analyses: number;
  counterfeit_count: number;
  authentic_count: number;
  suspicious_count: number;
  llm_provider: string;
  priority: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface BulkAnalysisResult {
  id: string;
  testProduct: {
    name: string;
    brand: string;
    category: string;
    url?: string;
    image?: string;
  };
  referenceProduct?: {
    id: string;
    name: string;
    brand: string;
    category: string;
    image?: string;
    confidence: number;
  };
  analysis: {
    is_counterfeit: boolean;
    confidence: number;
    risk_level: 'low' | 'medium' | 'high';
    risk_score: number;
    image_similarity_score: number;
    text_similarity_score: number;
    llm_analysis_score: number;
    key_differences: string[];
    recommendations: string[];
    detailed_analysis: {
      total_analyses: number;
      successful_analyses: number;
      failed_analyses: number;
      processing_status: string;
      error_message?: string;
    };
  };
  processingTime: number;
  analysisDate: string;
}

export interface BulkAnalysisResultsResponse {
  query_id: string;
  summary: {
    totalProducts: number;
    counterfeitCount: number;
    authenticCount: number;
    suspiciousCount: number;
    averageConfidence: number;
    averageProcessingTime: number;
    analysisDate: string;
    queryType: 'url' | 'csv';
    queryContent: string;
  };
  results: BulkAnalysisResult[];
  bulk_query: {
    status: string;
    llm_provider: string;
    created_at: string;
    completed_at?: string;
  };
}

// Bulk Analysis API Functions
export const bulkAnalysisApi = {
  // Submit a new bulk analysis query
  submitQuery: async (data: BulkAnalysisQuery): Promise<{
    success: boolean;
    query_id: string;
    task_id: string;
    status: string;
    message: string;
    query_type: string;
    llm_provider: string;
    created_at: string;
  }> => {
    const formData = new FormData();
    
    // Add basic fields
    formData.append('query_type', data.query_type);
    if (data.query_content) formData.append('query_content', data.query_content);
    if (data.urls) formData.append('urls', JSON.stringify(data.urls));
    if (data.llm_provider) formData.append('llm_provider', data.llm_provider);
    if (data.max_images_per_product) formData.append('max_images_per_product', data.max_images_per_product.toString());
    if (data.priority) formData.append('priority', data.priority);
    
    // Add CSV file if present
    if (data.csv_file) {
      formData.append('csv_file', data.csv_file);
    }
    
    // Add analysis config
    if (data.enableLLMAnalysis !== undefined) formData.append('enableLLMAnalysis', data.enableLLMAnalysis.toString());
    if (data.enableImageAnalysis !== undefined) formData.append('enableImageAnalysis', data.enableImageAnalysis.toString());
    if (data.enableTextAnalysis !== undefined) formData.append('enableTextAnalysis', data.enableTextAnalysis.toString());
    if (data.confidenceThreshold !== undefined) formData.append('confidenceThreshold', data.confidenceThreshold.toString());
    if (data.autoMatchProducts !== undefined) formData.append('autoMatchProducts', data.autoMatchProducts.toString());
    if (data.batchSize !== undefined) formData.append('batchSize', data.batchSize.toString());

    const response = await apiClient.post('/analyses/bulk-query/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get list of bulk analysis queries
  getQueries: async (): Promise<BulkAnalysisStatus[]> => {
    const response = await apiClient.get('/analyses/bulk-query/');
    return response.data;
  },

  // Get status of a specific bulk analysis query
  getStatus: async (queryId: string): Promise<{
    query_id: string;
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
    progress_percentage: number;
    current_step: string;
    total_products: number;
    products_processed: number;
    successful_analyses: number;
    failed_analyses: number;
    counterfeit_count: number;
    authentic_count: number;
    suspicious_count: number;
    success_rate: number;
    counterfeit_rate: number;
    created_at: string;
    started_at?: string;
    completed_at?: string;
    error_message?: string;
    celery_task_id?: string;
  }> => {
    const response = await apiClient.get(`/analyses/bulk-status/${queryId}/`);
    return response.data;
  },

  // Get results of a completed bulk analysis query
  getResults: async (queryId: string): Promise<BulkAnalysisResultsResponse> => {
    const response = await apiClient.get(`/analyses/bulk-results/${queryId}/`);
    return response.data;
  },

  // Cancel a running bulk analysis query
  cancelQuery: async (queryId: string): Promise<{
    success: boolean;
    message: string;
    status: string;
  }> => {
    const response = await apiClient.post(`/analyses/bulk-cancel/${queryId}/`);
    return response.data;
  },
};

export default apiClient;